import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  LayoutDensity,
  SidePadding,
  TextSize,
  useLayoutPreferences,
} from '../contexts/LayoutPreferencesContext';
import { usePreferences, ViewModeValue } from '../contexts/PreferencesContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

/**
 * Profile → Display & Layout: layout density, horizontal side padding, and the
 * account-wide default view.
 *
 * Density and side padding are device-scoped (localStorage via
 * LayoutPreferencesContext) and drive the shared layout tokens. The default
 * view is account-scoped, so it follows the user to another browser — the copy
 * below draws that distinction deliberately.
 */
const DEFAULT_VIEW_OPTIONS: { value: ViewModeValue; label: string }[] = [
  { value: 'grid', label: 'Grid' },
  { value: 'list', label: 'List' },
];

const DENSITY_OPTIONS: { value: LayoutDensity; label: string }[] = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

const SIDE_PADDING_OPTIONS: { value: SidePadding; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'reduced', label: 'Reduced' },
  { value: 'edge', label: 'Edge-to-edge' },
];

const TEXT_SIZE_OPTIONS: { value: TextSize; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'small', label: 'Small' },
];

const groupSx = { flexWrap: 'wrap' } as const;

const DisplayLayoutSettingsSection: React.FC = () => {
  const {
    layoutDensity,
    sidePadding,
    textSize,
    setLayoutDensity,
    setSidePadding,
    setTextSize,
    resetLayoutPreferences,
  } = useLayoutPreferences();
  const { cardContentPaddingSx } = useLayoutTokens();
  const preferences = usePreferences();

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Display & Layout
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Density, side padding and text size are saved on this device. The
          default view is saved to your account, so it follows you to other
          browsers.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Layout density
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Tightens spacing only. Use Text size to change how large text is.
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={layoutDensity}
            aria-label="Layout density"
            sx={groupSx}
            onChange={(_event, value: LayoutDensity | null) => {
              if (value) setLayoutDensity(value);
            }}
          >
            {DENSITY_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Side padding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Edge-to-edge also removes the maximum content width, so pages fill
            the whole window instead of staying in a centred column.
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={sidePadding}
            aria-label="Side padding"
            sx={groupSx}
            onChange={(_event, value: SidePadding | null) => {
              if (value) setSidePadding(value);
            }}
          >
            {SIDE_PADDING_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Text size
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Scales typography across the app, independently of density.
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={textSize}
            aria-label="Text size"
            sx={groupSx}
            onChange={(_event, value: TextSize | null) => {
              if (value) setTextSize(value);
            }}
          >
            {TEXT_SIZE_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {preferences && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Default view
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Used by every section that has not been given its own view.
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={preferences.settings.default_view_mode}
              aria-label="Default view"
              sx={groupSx}
              onChange={(_event, value: ViewModeValue | null) => {
                if (value) preferences.setTyped('default_view_mode', value);
              }}
            >
              {DEFAULT_VIEW_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        <Button variant="outlined" onClick={resetLayoutPreferences}>
          Reset display settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default DisplayLayoutSettingsSection;
